import React from 'react';
import { VehiculeConfig, Competence, PersonnelConfig } from '../../../../shared/types/config';

interface VehiculeArmementSectionProps {
  vehiculeConfigDetails: VehiculeConfig;
  competences: Competence[];
  personnelGradeConfigs: PersonnelConfig[];
}

/** Bloc « Armement (type d'engin) » extrait de VehiculeDetailsModal.tsx. */
const VehiculeArmementSection: React.FC<VehiculeArmementSectionProps> = ({
  vehiculeConfigDetails,
  competences,
  personnelGradeConfigs,
}) => {
  const idsComp = vehiculeConfigDetails.id_competences_armement ?? [];
  const idsGrades = vehiculeConfigDetails.id_grades_priorite ?? [];
  const hasTypeArmement =
    !!vehiculeConfigDetails.fonction_depart?.trim() ||
    idsComp.length > 0 ||
    idsGrades.length > 0 ||
    vehiculeConfigDetails.specialiste;

  if (!hasTypeArmement) return null;

  return (
    <div className="mb-6">
      <h3 className="text-lg font-semibold text-white mb-4">Armement (type d&apos;engin)</h3>

      {vehiculeConfigDetails.fonction_depart?.trim() && (
        <div className="bg-gradient-to-r from-blue-900/40 to-gray-800 rounded-lg p-4 border border-blue-500/30 mb-4">
          <p className="text-xs text-blue-300/90 uppercase tracking-wide mb-1">Fonction au départ</p>
          <p className="text-white font-semibold text-lg">{vehiculeConfigDetails.fonction_depart}</p>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        {vehiculeConfigDetails.specialiste && (
          <span className="px-2 py-1 rounded text-xs bg-amber-500/20 text-amber-300 border border-amber-500/40">
            Véhicule spécialiste
          </span>
        )}
      </div>

      {idsComp.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-300 mb-2">
            Compétences requises pour armer ({idsComp.length})
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {idsComp.map((cid) => {
              const c = competences.find((x) => x.id === cid);
              return (
                <div key={cid} className="bg-gray-800 rounded-lg p-3 border border-blue-500/25">
                  <p className="text-white font-medium">{c?.nom ?? `Compétence #${cid}`}</p>
                  {c?.description && (
                    <p className="text-gray-500 text-xs mt-1 line-clamp-2">{c.description}</p>
                  )}
                  <span className="text-[10px] text-gray-600 mt-1 inline-block">ID {cid}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {idsGrades.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-300 mb-2">
            Priorité des grades sur l&apos;engin
          </h4>
          <ol className="list-decimal list-inside space-y-2">
            {idsGrades.map((gid, idx) => {
              const g = personnelGradeConfigs.find((p) => p.id === gid);
              return (
                <li key={`${gid}-${idx}`} className="text-gray-200">
                  <span className="font-medium text-white">{g?.grade ?? `Grade #${gid}`}</span>
                  {g?.grade_code && (
                    <span className="text-gray-500 text-sm ml-2">({g.grade_code})</span>
                  )}
                  <span className="text-[10px] text-gray-600 ml-2">config_personnel #{gid}</span>
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </div>
  );
};

export default VehiculeArmementSection;
